/// <amd-module name="Wiki/WitQuery/WitQueryResultsTableView" />
import * as React from "react";

import { announce } from "VSS/Core/Util/Accessibility";
import { ICancelReason } from "VSS/Core/Util/Promise";
import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Link } from "VSSUI/Link";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { css } from "VSSUI/Util";

import { ITfsPageService } from "Tfs/Platform/Page";

import {
    QueryExpand,
    QueryHierarchyItem,
    QueryType,
    WorkItemFieldReference,
    WorkItemLink,
    WorkItemQueryResult
} from "Wiki/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient } from "Wiki/Clients/Wit/RestClient/WorkItemTracking";
import {
    EmptyQueryId,
    ErrorInFetchingQueryResults,
    NoResultsFoundMessage,
    OnlySharedQuerySupported,
    QueryNotFound,
    ShowingFirstNResults,
    ShowingNResults,
    ShowingOneResult,
    ViewWorkItemQuery
} from "Wiki/WitQuery/Resources";
import { TelemetryConstants, WitQueryErrorTypes, WitQueryTableConstants } from "Wiki/WitQuery/WitConstants";
import { getWorkItemGridColumnsForTree } from "Wiki/WitQuery/WitQueryUtils";
import { WorkItemTrackingDataProvider } from "Wit/UI/Data";
import { WorkItemTreeGrid } from "Wit/UI/WorkItemTreeGrid";

interface IErrorType {
    status: number;
}

export interface IWitQueryResultsTableProps extends IVssComponentProperties {
    queryId: string;
    isEditMode: Boolean;
}

export interface IWitQueryResultsTableState {
    queryCountMessage?: string;
    errorMessage?: string;
    errorType?: WitQueryErrorTypes;
    workItemIds?: number[];
    workItemRelations?: WorkItemLink[] | undefined;
    queryType?: QueryType;
    columns?: WorkItemFieldReference[];
}

const messageClassName = "text-message";

export class WitQueryResultsTableView extends VssComponent<IWitQueryResultsTableProps, IWitQueryResultsTableState> {
    private _workItemTrackingDataProvider: WorkItemTrackingDataProvider;
    private _projectId: string | undefined;
    private _queryUrl: string | undefined;
    private _viewQueryLinkRef = React.createRef<Link>();

    constructor(props: IWitQueryResultsTableProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {};
        const tfsPageData = this.context.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        let projectName: string | undefined;
        if (tfsPageData && tfsPageData.project) {
            this._projectId = tfsPageData.project.id;
            projectName = tfsPageData.project.name;
        }
        this._workItemTrackingDataProvider = new WorkItemTrackingDataProvider(this.context.pageContext, this._projectId);
        this._queryUrl = projectName
            ? routeUrl(this.context.pageContext, "ms.vss-work-web.query-route", {
                  project: projectName,
                  view: "query",
                  id: props.queryId
              })
            : undefined;
    }

    public render(): JSX.Element {
        const { queryCountMessage, errorMessage, errorType, workItemIds, queryType, workItemRelations, columns } = this.state;
        let content: JSX.Element | undefined;
        if (errorMessage) {
            content = (
                <>
                    {errorType === WitQueryErrorTypes.QueryNotFound ? (
                        <div className="flex-row flex-center">
                            <Status className="icon-margin" size={StatusSize.m} {...Statuses.Warning} />
                            <FormatComponent elementType={"div"} className={messageClassName} format={errorMessage}>
                                <span className="monospaced-text">{this.props.queryId}</span>
                            </FormatComponent>
                        </div>
                    ) : (
                        _getMessageComponent(errorMessage)
                    )}
                    {errorType === WitQueryErrorTypes.OnlySharedQuerySupported ? this._getViewQueryComponent(this._queryUrl) : null}
                </>
            );
        } else if (workItemIds && queryType && columns) {
            content = (
                <>
                    <div className="flex-row result-count-info">
                        {queryCountMessage ? _getMessageComponent(queryCountMessage) : null}
                        {this._getViewQueryComponent(this._queryUrl)}
                    </div>
                    {workItemIds ? (
                        <div className="workitem-grid-wrapper">
                            <WorkItemTreeGrid // this is not virualized (removed containerElement), as max count we have is only 200.
                                className="wit-query-grid"
                                dataProvider={this._workItemTrackingDataProvider}
                                itemsSource={workItemIds}
                                columns={getWorkItemGridColumnsForTree(columns)}
                                queryType={queryType}
                                workItemRelations={workItemRelations}
                                showLines={true}
                            />
                        </div>
                    ) : null}
                    {workItemIds && workItemIds.length === 0 ? _getMessageComponent(NoResultsFoundMessage, "no-results-message") : null}
                </>
            );
        }

        return <div className={"wiki-wit-table-container"}>{content}</div>;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (this._projectId) {
            this._fetchQueryResults(this.props.queryId, this._projectId);
        }
    }

    private _fetchQueryResults(queryId: string, projectId: string): void {
        if (!queryId) {
            this._updateErrorState(WitQueryErrorTypes.EmptyQueryId);
            return;
        }
        const witRestClient = getWorkItemTrackingClient(this.context.pageContext);
        this.trackPromise(witRestClient.getQuery(projectId, queryId, QueryExpand.Wiql, 1)).promise.then(
            (queryHierarchyItem: QueryHierarchyItem) => {
                if (queryHierarchyItem.isPublic === false) {
                    this._updateErrorState(WitQueryErrorTypes.OnlySharedQuerySupported);
                } else {
                    this.trackPromise(
                        witRestClient.queryById(queryId, projectId, undefined, undefined, WitQueryTableConstants.MaxNumberOfWorkItems + 1) // fetch one extra to check if more items exist
                    ).promise.then(
                        workItemQueryResult => {
                            const workItemIds = this._getWorkItemIds(workItemQueryResult);
                            this._updateState(workItemQueryResult, workItemIds);

                            // Publish telemetry for count of wi
                            this._publish(TelemetryConstants.WitQueryAttachWitCount, {
                                numberOfWitItems: workItemIds.length,
                                queryType: workItemQueryResult.queryType
                            });
                            if (this.props.isEditMode && this._viewQueryLinkRef.current) {
                                this._viewQueryLinkRef.current.focus();
                            }
                        },
                        (error: IErrorType | ICancelReason) => {
                            this._handleError(error);
                        }
                    );
                }
            },
            (error: IErrorType | ICancelReason) => {
                this._handleError(error);
            }
        );
    }

    private _handleError(error: IErrorType | ICancelReason): void {
        if ((error as ICancelReason).isCanceled) {
            return;
        }
        const errorType = (error as IErrorType).status === 404 ? WitQueryErrorTypes.QueryNotFound : WitQueryErrorTypes.UnknownError;
        this._updateErrorState(errorType);
    }

    private _updateErrorState(errorType: WitQueryErrorTypes): void {
        this.setState({
            errorMessage: _getErrorMessage(errorType),
            errorType
        });

        // publish telemetry for error type.
        this._publish(TelemetryConstants.WitQueryAttachFailed, { errorType });
    }

    private _getWorkItemIds(workItemQueryResult: WorkItemQueryResult): number[] {
        let workItemIds: number[] = [];
        if (workItemQueryResult.workItems) {
            workItemIds = workItemQueryResult.workItems.map<number>(workItem => workItem.id);
        } else if (workItemQueryResult.workItemRelations) {
            const workItemRelations = workItemQueryResult.workItemRelations;
            // Root nodes do not have a source.
            const rootWorkItemNodes = workItemRelations && workItemRelations.filter(relation => !relation.source);
            workItemIds = rootWorkItemNodes.map(workItemNode => workItemNode.target.id);
        }
        return workItemIds;
    }

    private _updateState(workItemQueryResult: WorkItemQueryResult, workItemIds: number[]): void {
        const queryCountMessage = _getCountMessage(workItemIds);
        if (this.props.isEditMode) {
            announce(queryCountMessage);
        }
        // remove the extra entries if the returned workItems are more than expected max number.
        const expectedWorkItems =
            workItemIds.length > WitQueryTableConstants.MaxNumberOfWorkItems
                ? workItemIds.slice(0, WitQueryTableConstants.MaxNumberOfWorkItems)
                : workItemIds;
        this.setState({
            workItemIds: expectedWorkItems,
            workItemRelations: workItemQueryResult.workItemRelations,
            queryType: workItemQueryResult.queryType,
            columns: workItemQueryResult.columns,
            queryCountMessage
        });
    }
    private _getViewQueryComponent(queryUrl?: string): JSX.Element | null {
        return queryUrl ? (
            <Link href={queryUrl} className="link-text" target={"_blank"} onClick={this._viewQueryClicked} ref={this._viewQueryLinkRef}>
                {ViewWorkItemQuery}
            </Link>
        ) : null;
    }

    private _viewQueryClicked = (): void => {
        this._publish(TelemetryConstants.WitQueryAttachViewQueryClicked, {});
    };

    private _publish(feature: string, properties: { [key: string]: any }): void {
        const service = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        service.publishEvent(TelemetryConstants.Area, feature, {
            queryId: this.props.queryId,
            projectId: this._projectId,
            isEditMode: this.props.isEditMode,
            ...properties
        });
    }
}

function _getMessageComponent(message: string, className?: string): JSX.Element {
    return <span className={css(messageClassName, className)}> {message} </span>;
}

// Exporting for UT
export function _getCountMessage(workItemIds: number[]): string | undefined {
    const count = workItemIds.length;
    let message: string | undefined;
    if (count <= 0) {
        return;
    } else if (count === 1) {
        message = ShowingOneResult;
    } else if (count <= WitQueryTableConstants.MaxNumberOfWorkItems) {
        message = format(ShowingNResults, count);
    } else {
        // returned count is more than expected max count.
        message = format(ShowingFirstNResults, WitQueryTableConstants.MaxNumberOfWorkItems);
    }
    return message;
}

// Exporting for UT
export function _getErrorMessage(errorType: WitQueryErrorTypes): string | undefined {
    let errorMessage: string | undefined;
    switch (errorType) {
        case WitQueryErrorTypes.OnlySharedQuerySupported:
            errorMessage = OnlySharedQuerySupported;
            break;
        case WitQueryErrorTypes.QueryNotFound:
            errorMessage = QueryNotFound;
            break;
        case WitQueryErrorTypes.EmptyQueryId:
            errorMessage = EmptyQueryId;
            break;
        case WitQueryErrorTypes.UnknownError:
            errorMessage = ErrorInFetchingQueryResults;
            break;
        default:
            errorMessage = undefined;
            break;
    }
    return errorMessage;
}

VssComponent.register("Wiki.WitQueryTableComponent", WitQueryResultsTableView);
